/**
 * TooltipSystem.ts — Contextual tooltip rendering for game elements.
 *
 * Renders bordered tooltip boxes for stats, temperaments, and other
 * game elements. Supports both blocking (modal) and non-blocking
 * (overlay) display modes.
 */

import type { Terminal } from './Terminal';
import type { InputManager } from './InputManager';
import type { ColorName } from './ColorTheme';

// ─── Tooltip Types ──────────────────────────────────────────

export interface TooltipDefinition {
    /** Element being described */
    subject: string;
    /** Short description (1-2 lines) */
    description: string[];
    /** Optional gameplay effect description */
    effect?: string;
    /** Color theme for the tooltip */
    color: ColorName;
}

export interface TooltipOptions {
    /** Row to render tooltip at (or 'auto' for near cursor) */
    row: number | 'auto';
    /** Column to render tooltip at (or 'auto' for centered) */
    col: number | 'auto';
    /** Maximum width of tooltip box */
    maxWidth?: number;
}

export interface TooltipSystemOptions {
    terminal: Terminal;
    inputManager: InputManager;
}

// ─── TooltipSystem Class ────────────────────────────────────

export class TooltipSystem {
    private terminal: Terminal;
    private inputManager: InputManager;
    private savedRegion: { row: number; col: number; width: number; height: number } | null = null;

    constructor(options: TooltipSystemOptions) {
        this.terminal = options.terminal;
        this.inputManager = options.inputManager;
    }

    /**
     * Show a tooltip and wait for dismissal (blocking).
     */
    async show(tooltip: TooltipDefinition, options: TooltipOptions = { row: 'auto', col: 'auto' }): Promise<void> {
        this.render(tooltip, options);
        await this.waitForAnyKey();
        this.clear();
    }

    /**
     * Wait for any key press using InputManager's anyKey mode.
     */
    private waitForAnyKey(): Promise<void> {
        return new Promise((resolve) => {
            // Double-rAF to guarantee execution after MenuSystem.drain() completes.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const previousMode = this.inputManager.getMode();
                    this.inputManager.setMode('anyKey');

                    const handler = (event: { type: string }) => {
                        if (event.type === 'anyKey') {
                            this.inputManager.off(handler);
                            this.inputManager.setMode(previousMode);
                            resolve();
                        }
                    };

                    this.inputManager.on(handler);
                });
            });
        });
    }

    /**
     * Render tooltip without blocking (for hover states).
     * Call clear() to remove the tooltip.
     */
    render(tooltip: TooltipDefinition, options: TooltipOptions = { row: 'auto', col: 'auto' }): void {
        const { terminal } = this;
        const { cols, rows } = terminal.getSize();
        const maxWidth = options.maxWidth ?? 50;

        // Calculate content dimensions
        const lines = this.formatTooltipContent(tooltip, maxWidth - 4);
        const contentWidth = Math.max(
            tooltip.subject.length + 2,
            ...lines.map(l => l.length)
        );
        const boxWidth = Math.min(maxWidth, contentWidth + 4);
        const boxHeight = lines.length + 3; // +2 for top/bottom borders, +1 for dismiss hint

        // Calculate position
        const row = options.row === 'auto'
            ? Math.max(2, Math.floor((rows - boxHeight) / 2))
            : options.row;
        const col = options.col === 'auto'
            ? Math.max(2, Math.floor((cols - boxWidth) / 2))
            : options.col;

        // Save region info for clearing
        this.savedRegion = { row, col, width: boxWidth, height: boxHeight };

        // Draw box with title
        this.drawTooltipBox(row, col, boxWidth, boxHeight, tooltip.subject, tooltip.color);

        // Draw content
        for (let i = 0; i < lines.length; i++) {
            terminal.setCursor(row + 1 + i, col + 2);
            terminal.write(lines[i], 'white');
        }

        // Draw dismiss hint
        terminal.setCursor(row + lines.length + 1, col + 2);
        terminal.write('Press any key to close', 'khaki');
    }

    /**
     * Clear any rendered tooltip.
     */
    clear(): void {
        if (!this.savedRegion) return;

        const { row, col, width, height } = this.savedRegion;
        this.terminal.clearRegion(row, row + height - 1, col, col + width - 1);
        this.savedRegion = null;
    }

    // ─── Private Methods ────────────────────────────────────────

    /**
     * Format tooltip content into lines that fit within maxWidth.
     */
    private formatTooltipContent(tooltip: TooltipDefinition, maxWidth: number): string[] {
        const lines: string[] = [];

        // Add description lines
        for (const desc of tooltip.description) {
            lines.push(...this.wrapText(desc, maxWidth));
        }

        // Add effect line if present
        if (tooltip.effect) {
            if (lines.length > 0) {
                lines.push(''); // Blank line before effect
            }
            lines.push(...this.wrapText(`→ ${tooltip.effect}`, maxWidth));
        }

        return lines;
    }

    /**
     * Wrap text to fit within maxWidth.
     */
    private wrapText(text: string, maxWidth: number): string[] {
        if (text.length <= maxWidth) return [text];

        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = '';

        for (const word of words) {
            if (currentLine.length + word.length + 1 <= maxWidth) {
                currentLine += (currentLine ? ' ' : '') + word;
            } else {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
            }
        }
        if (currentLine) lines.push(currentLine);

        return lines;
    }

    /**
     * Draw the tooltip box frame with title.
     */
    private drawTooltipBox(
        row: number,
        col: number,
        width: number,
        height: number,
        title: string,
        color: ColorName
    ): void {
        const { terminal } = this;
        const innerWidth = width - 2;

        // Top border with title
        terminal.setCursor(row, col);
        const titlePadded = ` ${title} `;
        const leftPad = Math.floor((innerWidth - titlePadded.length) / 2);
        const rightPad = innerWidth - titlePadded.length - leftPad;

        terminal.write('╭', color);
        terminal.write('─'.repeat(Math.max(0, leftPad)), color);
        terminal.write(titlePadded, color);
        terminal.write('─'.repeat(Math.max(0, rightPad)), color);
        terminal.write('╮', color);

        // Side borders with clear interior
        for (let r = 1; r < height - 1; r++) {
            terminal.setCursor(row + r, col);
            terminal.write('│', color);
            terminal.write(' '.repeat(innerWidth), 'white');
            terminal.write('│', color);
        }

        // Bottom border
        terminal.setCursor(row + height - 1, col);
        terminal.write('╰' + '─'.repeat(innerWidth) + '╯', color);
    }
}
